import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader, Section, AnchorPills, Stat, ArrowLink, Body, Swash } from "@/components/shared/PageHeader";
import { CaptionedFigure } from "@/components/shared/CaptionedFigure";
import { PhotoMosaic } from "@/components/shared/PhotoMosaic";
import { PhotoNumberTag } from "@/components/shared/PhotoNumber";
import { FadeUp } from "@/components/shared/FadeUp";
import { fetchBoard, fetchContent, fetchGroups, fetchSite } from "@/lib/api";
import { paragraphs } from "@/lib/format";
import { Seo } from "@/components/shared/Seo";
import { Img, SIZES } from "@/components/shared/Img";

const Prose = ({ text, lead = false }) => (
  <div className="prose-nn text-pretty">
    {paragraphs(text).map((p, i) => (
      <p key={i} className={lead && i === 0 ? "font-display text-[clamp(22px,2vw,30px)] font-medium leading-[1.25] tracking-[-0.02em]" : "text-[17px] leading-relaxed text-foreground/85"}>{p}</p>
    ))}
  </div>
);

const initials = (name = "") => name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const isDeputy = (m) => /vara/i.test(m?.role || "");

/**
 * The board as a roster on hairlines: role in small caps, the name large, contact details if the board has
 * chosen to publish them. The chair carries the gold dot. Deputies follow on one line beneath.
 */
const BoardRoster = ({ members }) => {
  const sorted = [...members].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const board = sorted.filter((m) => !isDeputy(m));
  const deputies = sorted.filter(isDeputy);
  if (!sorted.length) return <p className="text-muted-foreground" data-testid="board-empty">Styret vert presentert her så snart lista er klar.</p>;
  return (
    <div data-testid="board-roster">
      <ol className="grid border-t border-border sm:grid-cols-2 lg:grid-cols-3" data-testid="board-grid">
        {board.map((m, i) => (
          <li key={m.id} className="flex items-start justify-between gap-5 border-b border-border py-6 pr-6" data-testid={`board-member-${m.id}`}>
            <div className="min-w-0">
              <p className="t-mono flex items-center gap-2 text-muted-foreground">
                {i === 0 && <span className="h-1.5 w-1.5 rounded-full bg-sol" aria-hidden="true" />}
                <span>{m.role}</span>
              </p>
              <h3 className="mt-2.5 font-display text-[clamp(20px,1.5vw,25px)] font-semibold leading-[1.1] tracking-[-0.02em] text-balance" data-testid="board-member-name">{m.placeholder ? "Kjem" : m.name}</h3>
              {(m.email || m.phone) && (
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[14px]">
                  {m.email && <a href={`mailto:${m.email}`} className="break-all u-link">{m.email}</a>}
                  {m.phone && <a href={`tel:${m.phone}`} className="tabular u-link">{m.phone}</a>}
                </div>
              )}
            </div>
            {m.image ? (
              <span className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-secondary"><Img src={m.image} alt="" width={240} className="h-full w-full object-cover" /></span>
            ) : (
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-[13px] font-medium text-muted-foreground" aria-hidden="true">{m.placeholder ? <Users className="h-4 w-4" /> : initials(m.name)}</span>
            )}
          </li>
        ))}
      </ol>
      {deputies.length > 0 && (
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-baseline sm:gap-10" data-testid="board-deputies">
          <p className="t-mono shrink-0 text-muted-foreground">Varamedlemmer</p>
          <ul className="flex flex-wrap gap-x-8 gap-y-2 text-[17px] font-medium">
            {deputies.map((m) => <li key={m.id} data-testid={`board-deputy-${m.id}`}>{m.name}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function About() {
  const { data: content, isLoading } = useQuery({ queryKey: ["content"], queryFn: fetchContent });
  const { data: board } = useQuery({ queryKey: ["board"], queryFn: fetchBoard });
  const { data: groups } = useQuery({ queryKey: ["groups"], queryFn: fetchGroups });
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  const om = content?.om_huset;
  const vel = content?.velforeininga;
  const styret = content?.styret_intro;
  const faste = content?.faste_brukarar_intro;
  const email = site?.contact_email || "post@halsnoykulturhus.no";
  const facade = om?.images?.[0];
  const cap = (block, src) => (src && block?.meta?.captions?.[src]) || "";
  cap.all = (block) => block?.meta?.captions || {};
  // Velforeininga: dekking før fest i Stovene – unless the board has chosen another picture in admin.
  const velOwn = (vel?.images || []).find((src) => typeof src === "string" && src.startsWith("/api/images/"));
  const velImage = velOwn || vel?.images?.[0] || "/api/images/b556fdd6-c2e8-43d8-af79-878aeedcd9d4";

  return (
    <div data-testid="about-page">
      <Seo title="Om huset" description="Halsnøy Kulturhus SA er eit samvirkeføretak eigd av folket på øya og drive på dugnad. Les om huset, styret, Velforeininga og dei faste brukarane." image={facade} imageAlt="Halsnøy Kulturhus" />
      <PageHeader
        eyebrow="Om oss"
        title={<Swash text="Eit hus bygd og drive av *folket*" />}
        intro="Halsnøy Kulturhus SA er eit samvirkeføretak eigd av folket på øya og drive på dugnad. Her finn du huset, styret, Velforeininga og dei faste brukarane."
      >
        <AnchorPills testPrefix="about-anchor" items={[["#huset", "Huset"], ["#styret", "Styret"], ["#velforeininga", "Velforeininga"], ["#faste-brukarar", "Faste brukarar"]]} />
      </PageHeader>

      {/* HUSET */}
      <Section id="huset" eyebrow="Huset" className="pt-6 sm:pt-8 lg:pt-10">
        {isLoading ? <Skeleton className="h-64 rounded-[12px]" /> : (
          <>
            <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-7">
                <h2 className="t-h2 max-w-3xl text-balance">{om?.title}</h2>
                <div className="mt-8 max-w-3xl"><Prose text={om?.body} lead /></div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button asChild size="lg" data-testid="about-rooms-link"><Link to="/rom">Sjå romma <ArrowUpRight className="h-4 w-4" /></Link></Button>
                  <Button asChild variant="outline" size="lg" data-testid="about-prices-link"><Link to="/prisar">Prisliste</Link></Button>
                  <Button asChild variant="outline" size="lg" data-testid="about-history-link"><Link to="/historie">Historia til huset</Link></Button>
                </div>
              </div>
              {facade && (
                <FadeUp className="lg:col-span-5">
                  <CaptionedFigure src={facade} caption={cap(om, facade)} alt="Halsnøy Kulturhus i Sæbøvik" width={1200} sizes="(min-width: 1440px) 560px, (min-width: 1024px) 40vw, 100vw" imgClassName="aspect-[4/3] [object-position:40%_50%] lg:aspect-square" testId="about-facade" />
                  {!cap(om, facade) && <p className="mt-3 text-[13px] text-muted-foreground">Kulturhuset i Sæbøvik – amfi, kafé, stover og møterom under same tak.</p>}
                </FadeUp>
              )}
            </div>
            {(om?.images?.length || 0) > 1 && <FadeUp className="mt-12 sm:mt-14"><PhotoMosaic images={om.images.slice(1)} captions={cap.all(om)} alt={om?.title || "Huset"} testId="about-huset-gallery" /></FadeUp>}
            <div className="cellgrid mt-14 grid-cols-2 border-t border-border pt-10 lg:mt-16 lg:grid-cols-4" data-testid="about-stats">
              <Stat value="187" label="plassar i amfiet" />
              <Stat value="3" label="etasjar" />
              <Stat value="7" label="rom til leige" />
              <Stat value="0" label="faste tilsette" />
            </div>
          </>
        )}
      </Section>

      {/* STYRET – a roster on hairlines: the board, then the deputies */}
      <Section id="styret" eyebrow="Styret" title={styret?.title || "Styret"} subtitle={paragraphs(styret?.body)[0]}>
        <BoardRoster members={board || []} />
      </Section>

      {/* VELFOREININGA */}
      <Section id="velforeininga" dark eyebrow="Velforeininga">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            <h2 className="t-h2 text-balance text-paper">{vel?.title}</h2>
            <div className="prose-nn mt-6 text-[17px] leading-relaxed text-paper/70 text-pretty">{paragraphs(vel?.body).map((p, i) => <p key={i}>{p}</p>)}</div>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild variant="light" size="lg" data-testid="about-volunteer-link"><Link to="/velforeininga">Om Velforeininga <ArrowUpRight className="h-4 w-4" /></Link></Button>
              <Button asChild variant="lightOutline" size="lg" data-testid="about-volunteer-contact"><a href={`mailto:${email}?subject=Eg%20vil%20bidra%20p%C3%A5%20dugnad`}>Eg vil bidra</a></Button>
            </div>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <CaptionedFigure src={velImage} caption={cap(vel, velImage)} alt="Dekking av langbord før fest i Stovene" width={800} sizes={SIZES.third} imgClassName="aspect-[4/3] lg:aspect-[4/5]" radius="rounded-[20px]" testId="vel-figure" />
          </div>
        </div>
        {(vel?.images || []).filter((s) => s !== velImage).length > 0 && <PhotoMosaic tone="dark" images={vel.images.filter((s) => s !== velImage)} captions={cap.all(vel)} alt={vel?.title || "Velforeininga"} className="mt-12" testId="about-vel-gallery" />}
      </Section>

      {/* FASTE BRUKARAR */}
      <Section id="faste-brukarar" eyebrow="Faste brukarar" title={faste?.title || "Faste brukarar av huset"} subtitle={paragraphs(faste?.body)[0]} action={<ArrowLink to="/kalender">Sjå faste øvingar</ArrowLink>}>
        <Body>
          <ul className="cellgrid sm:grid-cols-2 lg:grid-cols-3" data-testid="groups-grid">
            {(groups || []).map((g) => (
              <li key={g.id} className="flex flex-col" data-testid={`group-card-${g.id}`}>
                {g.image && <figure className="img-hover relative mb-5 overflow-hidden rounded-[12px] bg-secondary" data-testid={`group-image-${g.id}`}><Img src={g.image} alt={g.name} width={800} sizes={SIZES.third} className="aspect-[4/3] w-full object-cover" /><PhotoNumberTag src={g.image} interactive className="absolute right-2.5 top-2.5" testId={`group-image-number-${g.id}`} /></figure>}
                <h3 className="t-h3">{g.name}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{g.description}</p>
                {g.website && <div className="mt-auto pt-5"><ArrowLink href={g.website} target="_blank" rel="noopener noreferrer" up className="text-sm">Nettside</ArrowLink></div>}
              </li>
            ))}
          </ul>
          <div className="mt-12 flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[15px] text-muted-foreground">Vil laget ditt bli fast brukar av huset? Vi finn ei ordning som passar.</p>
            <ArrowLink to="/kontakt">Ta kontakt</ArrowLink>
          </div>
        </Body>
      </Section>

    </div>
  );
}
